import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface AdeDevice {
  id: string;
  serial_number: string;
  model: string | null;
  os: string | null;
  op_type: string | null;
  device_id: string | null;
  abm_connection_id: string;
  profile_uuid: string | null;
  profile_assign_time: string | null;
  device_assigned_date: string | null;
  device_assigned_by: string | null;
  sync_status: string | null;
  metadata: any | null;
  last_synced_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  device?: {
    name: string;
    status: string;
  };
}

export const useAdeDevices = (abmConnectionId?: string) => {
  const [devices, setDevices] = useState<AdeDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [syncing, setSyncing] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (abmConnectionId) {
      fetchDevices();
    } else {
      setLoading(false);
    }
  }, [abmConnectionId]);

  const fetchDevices = async () => {
    if (!abmConnectionId) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('ade_devices')
        .select(`
          *,
          device:devices(
            name,
            status
          )
        `)
        .eq('abm_connection_id', abmConnectionId)
        .order('created_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      setDevices(data || []);
    } catch (err) {
      console.error('Error fetching ADE devices:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch ADE devices'));
      toast({
        title: 'Error',
        description: 'Failed to load ADE devices',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const syncDevices = async () => {
    if (!abmConnectionId) return;

    try {
      setSyncing(true);

      const { data, error: syncError } = await supabase.functions.invoke('sync-abm-devices', {
        body: { abm_connection_id: abmConnectionId },
      });

      if (syncError) {
        throw syncError;
      }

      toast({
        title: 'Success',
        description: `Synced ${data?.synced_count || 0} devices from Apple Business Manager`,
      });

      await fetchDevices();
    } catch (err) {
      console.error('Error syncing ADE devices:', err);
      toast({
        title: 'Error',
        description: 'Failed to sync devices from Apple Business Manager',
        variant: 'destructive',
      });
      throw err;
    } finally {
      setSyncing(false);
    }
  };

  const assignProfile = async (deviceIds: string[], profileUuid: string) => {
    if (!abmConnectionId) return;

    try {
      const { data, error: assignError } = await supabase.functions.invoke('assign-ade-profile', {
        body: {
          abm_connection_id: abmConnectionId,
          device_ids: deviceIds,
          profile_uuid: profileUuid,
        },
      });

      if (assignError) {
        throw assignError;
      }

      toast({
        title: 'Success',
        description: `Enrollment profile assigned to ${deviceIds.length} device(s)`,
      });

      await fetchDevices();
    } catch (err) {
      console.error('Error assigning profile to devices:', err);
      toast({
        title: 'Error',
        description: 'Failed to assign enrollment profile to devices',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const getDevicesByProfile = (profileUuid: string | null) => {
    return devices.filter(device => device.profile_uuid === profileUuid);
  };

  const getUnassignedDevices = () => {
    return devices.filter(device => !device.profile_uuid);
  };

  return {
    devices,
    loading,
    error,
    syncing,
    fetchDevices,
    syncDevices,
    assignProfile,
    getDevicesByProfile,
    getUnassignedDevices,
  };
};
